import { AtClickable, AtIcon, AtText } from '@/components/atoms';
import {
  TClickableSize,
  EClickableTarget,
  EClickableVariants,
} from '@/components/atoms/at-clickable/at-clickable.types';
import { MlCopyBlock } from '@/components/molecules';
import {
  IIconCardItem,
  IListItem,
  IOrContentBlockCSProps,
  EBlockAlignment,
  EThemes,
} from '@/lib/contentstack-sdk/types/component';
import { MlCard } from '@/components/molecules';
import { OrContentBlock } from './or-content-block';
import { RenderRichText } from '@/components/helpers/cms/RenderRichText';
import { EIcons } from '@/components/atoms/at-icon/icons';
import atClickable from '@/components/atoms/at-clickable/at-clickable.variants';
import { TOrContentBlockCS } from './or-content-block.types';

export function isIconCardItem(
  item: IIconCardItem | IListItem,
): item is IIconCardItem {
  return 'icon_card' in item;
}

export function isListItem(item: IIconCardItem | IListItem): item is IListItem {
  return 'list' in item;
}

export const OrContentBlockCS = ({ block }: TOrContentBlockCS) => {
  if (!block) return null;

  const {
    theme: { theme },
    left_content,
    right_content,
    top_spacing,
    bottom_spacing,
  } = block;

  const RenderLeftContent = () => {
    if (left_content?.length <= 0) {
      return (
        <AtText className="text-skin-error">
          Please add a Copy Block instance
        </AtText>
      );
    }
    return (
      <>
        {left_content.map((items) => {
          const {
            title,
            block_header,
            description,
            link,
            uid,
            alignment,
            heading_element,
            heading_variant,
            eyebrow,
            subhead,
          } = items;

          const {
            size,
            icon,
            outline,
            target,
            url: { title: label, href },
            variant,
            type,
            aria_label,
          } = link;

          const iconName = icon?.[0]?.name?.icon;
          const iconPosition = icon?.[0]?.select?.iconPosition;
          const currentIcon = iconName as EIcons;
          const currentTarget = target as keyof typeof EClickableTarget;
          const currentVariant = variant as keyof typeof EClickableVariants;
          const renderIcon =
            icon &&
            icon.length > 0 &&
            EClickableTarget[currentTarget] !== EClickableTarget.BLANK;
          const { iconColor } = atClickable({
            type,
            intent: EClickableVariants[currentVariant],
            outline,
          });
          return (
            <MlCopyBlock
              header={block_header}
              eyebrow={eyebrow}
              subhead={subhead}
              title={title}
              key={uid}
              titleElement={heading_element?.select || 'h2'}
              titleVariant={
                heading_variant?.select || heading_element?.select || 'h2'
              }
              alignment={EBlockAlignment[alignment?.alignmentprop]}
            >
              <RenderRichText body={description} />
              <AtClickable
                variant={EClickableVariants[currentVariant]}
                size={size.toLowerCase() as TClickableSize}
                label={label}
                href={href}
                iconPosition={iconPosition}
                outline={outline}
                type={type}
                target={EClickableTarget[currentTarget]}
                ariaLabel={aria_label}
                interactLocation="or-content-block"
              >
                {renderIcon && (
                  <AtIcon color={iconColor()} icon={currentIcon} />
                )}
              </AtClickable>
            </MlCopyBlock>
          );
        })}
      </>
    );
  };
  const currentTheme = EThemes[theme] as EThemes;
  return (
    <OrContentBlock
      theme={currentTheme}
      RenderLeftContent={RenderLeftContent}
      data-testid="or-content-block"
      topSpacing={top_spacing?.spacing_value}
      bottomSpacing={bottom_spacing?.spacing_value}
      topDivider={top_spacing?.divider}
      bottomDivider={bottom_spacing?.divider}
    >
      {right_content.length > 0 ? (
        <>
          {right_content.map((contents) => {
            if (isIconCardItem(contents)) {
              const { description = '', title, icon } = contents.icon_card;
              return (
                <MlCard
                  key={`${contents.icon_card?._metadata?.uid} - ${title} `}
                  iconName={icon}
                  theme={currentTheme}
                  title={title}
                  body={description}
                  headerElement="h3"
                  className="[&_p]:paragraph-base rounded-none !px-0 [&_h3]:font-Figtree [&_p]:text-22 [&_p]:lg:text-2xl"
                />
              );
            }

            if (isListItem(contents)) {
              const {
                content,
                _metadata: { uid },
              } = contents.list;
              return <RenderRichText body={content} key={uid} />;
            }
          })}
        </>
      ) : (
        <AtText className="text-skin-error">Please add some content</AtText>
      )}
    </OrContentBlock>
  );
};
